import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { Video } from "@/components/common/video";
import { Spinner } from "@/components/ui/modal";
import VideoController from "./video-controller";
import CloseButton from "./close-button";
import PrevButton from "./prev-button";
import NextButton from "./next-button";
import Panel from "./right-panel";
import { useUserManagementHooks } from "@/manager/manager/agent-dashboard-user-management-manager";
import { ConfirmModal } from "../common/confirm-modal";

interface EditVideoPlayModalProps {
  open: boolean;
  videos: Actor[];
  initialIndex: number;
  voices: Audio[];
  onOpenChange: (open: boolean) => void;
}

export const EditVideoPlayModal: React.FC<EditVideoPlayModalProps> = ({
  open,
  onOpenChange,
  videos,
  initialIndex,
  voices,
}) => {
  const { t } = useTranslation();
  const { selectedActor, selectedActorFormData } = useUserManagementHooks.useSelectedActor();
  const { changeValue, save, loading: saveLoading, actorNameError, voiceNameError, voiceIdError, isAllowSave, duplicateNameMsg, resetError } = useUserManagementHooks.useSaveSelectedActor();
  const { isChanged, reset } = useUserManagementHooks.useSelectedActorFormDataChanged();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [load, setLoad] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentVideo = videos[currentIndex];

  // 同步初始索引
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (currentVideo) {
      selectedActor(currentVideo);
      resetError();
    }
  }, [currentVideo])

  const handleSave = async () => {
    try {
      await save();
      onOpenChange(false);
    } catch (error) {
      console.warn("Save failed:", error);
    }
  }

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    reset();
    onOpenChange && onOpenChange(false);
  }

  const handleOpenChange = () => {
    // 数据改变,打开确认弹窗
    setShowConfirmModal(isChanged);
    // 数据未改变,关闭音频弹窗
    onOpenChange(isChanged);
  }

  // 自动播放视频
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !open) return;

    const handleCanPlayThrough = async () => {
      try {
        await video.play();
      } catch (error) {
        console.warn("Auto-play failed:", error);
      }
    };

    video.addEventListener("canplaythrough", handleCanPlayThrough);

    // 如果视频已经可以播放，立即尝试播放
    if (video.readyState >= 3) {
      handleCanPlayThrough();
    }

    return () => {
      video.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, [open, currentVideo?.video]);

  // 上一个视频
  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + videos.length) % videos.length);
  };

  // 下一个视频
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % videos.length);
  };

  // 键盘导航
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case "ArrowLeft":
          handlePrevious();
          break;
        case "ArrowRight":
          handleNext();
          break;
        case "Escape":
          handleOpenChange();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  if (!currentVideo) return null;

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[95vw] h-[95vh] max-w-[1055px] max-h-[600px] rounded-2xl shadow-lg z-50 flex overflow-hidden">
          {/* 左侧视频区域 */}
          <div className="group flex-1 relative bg-black/20 backdrop-blur-3xl flex flex-col">
            {/* 关闭按钮 */}
            <Dialog.Close asChild>
              <CloseButton className="absolute top-4 right-4 z-10" />
            </Dialog.Close>

            {/* 导航按钮 */}
            {videos.length > 1 && (
              <div className="flex flex-col gap-2 absolute right-4 top-1/2 transform -translate-y-1/2 z-10">
                <PrevButton onClick={handlePrevious} />
                <NextButton onClick={handleNext} />
              </div>
            )}

            {/* 视频播放器 */}
            <div className="flex-1 flex items-center justify-center">
              <div className="absolute top-0 bottom-0 aspect-[9/16]">
                <Video
                  ref={videoRef}
                  src={currentVideo.video}
                  className="h-full"
                  poster={currentVideo.image}
                  autoPlay
                  preload="auto"
                />
              </div>
            </div>

            {load && (
              <div className="absolute inset-0 flex justify-center items-center bg-black/20">
                <Spinner className="text-white" size={32} />
              </div>
            )}

            {/* 视频控制器 */}
            <div className="opacity-0 group-hover:opacity-100 absolute bottom-0 left-0 right-0 transition-opacity duration-250">
              <VideoController videoRef={videoRef} onLoad={setLoad} />
            </div>
          </div>

          {/* 右侧信息面板 */}
          <Panel
            actor={currentVideo}
            voices={voices}
            onValueChange={changeValue}
            onSave={handleSave}
            actorNameError={actorNameError}
            voiceNameError={voiceNameError}
            saveLoading={saveLoading}
            isAllowSave={isAllowSave}
            duplicateNameMsg={duplicateNameMsg}
            isChanged={isChanged}
            {...selectedActorFormData}
          />
        </Dialog.Content>
      </Dialog.Portal>
      <ConfirmModal
        open={showConfirmModal}
        onOpenChange={setShowConfirmModal}
        title={t('agent_dashboard.confirm_modal.cancel_title')}
        description={t('agent_dashboard.confirm_modal.cancel_desc')}
        cancel={t('agent_dashboard.confirm_modal.cancel')}
        confirm={t('agent_dashboard.confirm_modal.confirm')}
        onConfirm={handleCancelConfirm}
      />
    </Dialog.Root>
  );
};